import React, { useEffect, useState } from "react";
import AssessmentSection from "../components/AssessmentSection";
import ReviewSection from "../components/ReviewSection";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getAssessment, getWarmup } from "../services/AssessmentService";
import { openAssignedContent } from "../services/AssignmentService";
import AppShell from "../components/ui/AppShell";
import { Card, EmptyState, TextLink } from "../components/ui";
import { Award, Quiz } from "../components/icons";

/**
 * One assessment on a page of its own.
 *
 * Reached two ways, and `:id` means something different in each:
 *
 * - Email invitation, `/assessment/:id/:type/:token` — the backend builds this
 *   link with the assessment's MODULE id, so it is looked up by module.
 * - From inside the app, `/assessment/:id/:type` — the learner is already
 *   signed in and `:id` is the assessment's OWN id. This is how assessments
 *   with no host PPT lesson (standalone, or whose host lesson was deleted) are
 *   opened from a learning path, since they have no section workspace to
 *   deep-link into.
 *
 * The in-app entry is a FIRST-CLASS destination, not a fallback: it carries the
 * same shell as every other learner screen, and it always renders something.
 * It previously gated the whole page on the fetched assessment having a name,
 * so a failed or empty fetch painted a blank white screen — the learner clicked
 * "Start" and, as far as they could tell, nothing happened. Loading and failure
 * are now states you can see, with a way back out of both.
 *
 * It also carries the section workspace's TWO modes, for the same reason that
 * workspace has them: the assessment itself, and the learner's record of it.
 * Without the second one a standalone assessment's "Review" had nowhere to go —
 * it landed on "Get ready for the challenge ahead!" and a Practice button, for
 * a test the learner had already passed.
 */

/** The two things this page can show: the paper, and the record of it. */
const MODES = {
  Assessment: { label: "Assessment", icon: Quiz, sub: "Questions & practice" },
  Review: { label: "Review", icon: Award, sub: "Your score & answers" },
};

function IndividualAssessment() {
  const { id, type, token } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [module, setModule] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | error
  const [showAssessment, setShowAssessment] = useState(false);
  // `?review=1` is set by whoever sent the learner here when the plan said
  // "Review" — see lib/learning.openAssignedItem. Read once, as the initial
  // mode, so the learner can switch freely afterwards without the URL fighting
  // them.
  const [mode, setMode] = useState(
    params.get("review") === "1" ? "Review" : "Assessment"
  );

  // Only the invitation link identifies content by module; without a token the
  // id is the assessment itself, so we must not resolve it by module.
  const byModule = !!token;
  // The invitation link is opened from an email, outside the app — there is no
  // session behind it to wrap in the learner shell.
  const inApp = !token;

  useEffect(() => {
    (async () => {
      setState("loading");
      setModule(null);
      try {
        if (!id) {
          setState("error");
          return;
        }
        if (token) localStorage.setItem("token", token);

        let response;
        if (type === "warmup") {
          response = await getWarmup(id);
        } else if (byModule) {
          response = await getAssessment(id);
        } else {
          // Opened from the learner's plan: fetch it on the strength of the
          // assignment, not the learner's browse scope, so an assigned
          // assessment is never unreachable.
          const payload = await openAssignedContent("assessment", id);
          response = payload?.assessment || null;
        }

        if (!response?.name) {
          setState("error");
          return;
        }

        setModule({
          ...response,
          title: response.name,
          // The lesson-scoped viewer keys off `_id`; for a module-resolved
          // assessment that is the module it belongs to.
          ...(type === "assessment" && byModule ? { _id: response.module } : {}),
        });
        setState("ready");
      } catch (error) {
        console.log(error);
        setState("error");
      }
    })();
  }, [id, type, token, byModule]);

  /**
   * The record is only offered where `:id` IS the assessment and the thing is a
   * real assessment:
   *
   * - the emailed link identifies content by MODULE, so `id` would send the
   *   review endpoint looking for the wrong document, and there is no session
   *   behind that link to have a record in;
   * - a warm-up is unscored practice, with no attempt history to report.
   */
  const canReview = inApp && !byModule && type !== "warmup";
  const activeMode = canReview ? mode : "Assessment";

  const body =
    state === "loading" ? (
      <p className="ds-sub">Loading this assessment…</p>
    ) : state === "error" ? (
      <Card>
        <EmptyState
          icon={Quiz}
          text="This assessment could not be loaded. It may have been unpublished, or it is no longer assigned to you — please contact your training team."
          action={
            inApp ? (
              <TextLink onClick={() => navigate("/my-learning")}>
                Back to My Learning Plan
              </TextLink>
            ) : null
          }
        />
      </Card>
    ) : activeMode === "Review" ? (
      // The same stage frame the section workspace puts Review in — the
      // component insets itself to sit inside one.
      <div className="ds-stage">
        {/* Keyed on the assessment itself: standing outside any lesson, there
            is no lesson id to ask the review endpoint about. */}
        <ReviewSection assessmentId={id} />
      </div>
    ) : (
      <AssessmentSection
        selectedModule={module}
        type={type}
        assessmentId={byModule ? undefined : id}
        hideNavigation={"yes"}
        goBack={setShowAssessment}
      />
    );

  // Emailed invitations land here with no session and no plan to go back to, so
  // they keep the bare page they have always had.
  if (!inApp) return <div>{body}</div>;

  return (
    <AppShell crumb={module?.name || "Assessment"} active="plan">
      <div className="ds-section__head" style={{ marginTop: 0 }}>
        <div>
          <h1 className="ds-h1">{module?.name || "Assessment"}</h1>
          <p className="ds-sub">
            Assigned to you — this one stands on its own, outside any module.
          </p>
        </div>
        <TextLink onClick={() => navigate("/my-learning")}>
          Back to My Learning Plan
        </TextLink>
      </div>

      {/* The same tiles the section workspace uses, so "Review" means the same
          thing and looks the same wherever the learner meets it. Hidden while
          the assessment is still loading or failed — there is nothing to switch
          between until we know what this is. */}
      {canReview && state === "ready" && (
        <div className="ds-modes">
          {Object.keys(MODES).map((key) => {
            const meta = MODES[key];
            const ModeIcon = meta.icon;
            return (
              <button
                key={key}
                type="button"
                className={`ds-mode${activeMode === key ? " ds-mode--active" : ""}`}
                onClick={() => setMode(key)}
              >
                <span className="ds-mode__icon">
                  <ModeIcon size={21} />
                </span>
                <span className="ds-mode__label">{meta.label}</span>
                <span className="ds-mode__sub">{meta.sub}</span>
              </button>
            );
          })}
        </div>
      )}

      {body}
    </AppShell>
  );
}

export default IndividualAssessment;
